import { useCallback, useEffect } from 'react';
import { openModal, openToast } from 'utils/localDispatcher';
import ChatOrderModal from 'components/chatMain/chatOrder/ChatOrderModal';
import ChatOrderAgreementModal from 'components/chatMain/chatOrder/ChatOrderAgreementModal';
import { useChatOrderEligibility } from 'hooks/useChatOrderEligibility';
import { DAANGN_BUSINESS_URL } from 'constants/config';

const CHATTING_WEB_POPUP_CLOSED_MESSAGE = 'chat-order-popup-closed';

interface UseChatOrderStartModalParams {
  baId: string;
  buyerId: string;
  buyerName: string;
}

export const useChatOrderStartModal = ({
  baId,
  buyerId,
  buyerName,
}: UseChatOrderStartModalParams) => {
  const { eligibility, refetch } = useChatOrderEligibility(baId);

  const storeId = eligibility?.store?._id;
  const isNotVerified = eligibility?.baById?.businessRegistration?.inspectionStatus !== 'APPROVED';
  const isNotRegisterAccountNumber = !eligibility?.baById?.settlement?.accountNumber;
  const isChatOrderTarget = eligibility?.baById?.isChatOrderTarget;
  const isChatOrderEnabled = eligibility?.baById?.saleMethodSetting?.chatOrderEnabled;

  const openRequirementsPopup = useCallback(() => {
    const width = 600;
    const height = 600;
    window.open(
      `${DAANGN_BUSINESS_URL}/profile/${baId}/popup/chat-order/requirements`,
      '_blank',
      `width=${width},height=${height}`
    );
  }, [baId]);

  const openAgreementModal = useCallback(() => {
    openModal({
      content: (
        <ChatOrderAgreementModal
          baId={Number(baId)}
          onComplete={(newStoreId) => {
            openModal({
              content: (
                <ChatOrderModal
                  buyerAccountId={buyerId}
                  storeId={newStoreId}
                  buyerName={buyerName}
                />
              ),
              isShow: true,
              disableBackdropClick: true,
            });
          }}
        />
      ),
      isShow: true,
    });
  }, [baId, buyerId, buyerName]);

  const openOrderModal = useCallback((targetStoreId: string) => {
    openModal({
      content: (
        <ChatOrderModal
          buyerAccountId={buyerId}
          storeId={targetStoreId}
          buyerName={buyerName}
        />
      ),
      isShow: true,
      disableBackdropClick: true,
    });
  }, [buyerId, buyerName]);

  const openChatOrderModal = useCallback(() => {
    if (!baId) {
      openToast('잠시 문제가 발생했어요.');
      return;
    }

    if (isNotVerified || isNotRegisterAccountNumber) {
      openRequirementsPopup();
      return;
    }

    if (!isChatOrderEnabled) {
      openAgreementModal();
      return;
    }

    openOrderModal(String(storeId));
  }, [baId, storeId, isNotVerified, isNotRegisterAccountNumber, isChatOrderEnabled, openRequirementsPopup, openAgreementModal, openOrderModal]);

  useEffect(() => {
    if (!buyerId) return;

    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== DAANGN_BUSINESS_URL) return;
      if (event.data === CHATTING_WEB_POPUP_CLOSED_MESSAGE) {
        const newEligibility = await refetch();
        const newIsNotVerified = newEligibility?.baById?.businessRegistration?.inspectionStatus !== 'APPROVED';
        const newIsNotRegisterAccountNumber = !newEligibility?.baById?.settlement?.accountNumber;
        const newIsChatOrderEnabled = newEligibility?.baById?.saleMethodSetting?.chatOrderEnabled;
        const newStoreId = String(newEligibility?.store?._id);

        if (newIsNotVerified || newIsNotRegisterAccountNumber) {
          return;
        }

        if (!newIsChatOrderEnabled) {
          openAgreementModal();
          return;
        }

        openOrderModal(newStoreId);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [buyerId, refetch, openAgreementModal, openOrderModal]);

  return {
    openChatOrderModal,
    isChatOrderButtonShow: isChatOrderTarget,
    isChatOrderEnabled,
  };
};
